import { Alert, Card, Button, List, Tag, Space, Typography, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { formatVND } from '@/utils/format';
import { ROUTES } from '@/constants/routes';

const { Text } = Typography;

/**
 * Khối phiếu thu của đơn hàng chỉ nói chuyện KẾ TOÁN NỘI BỘ: mã phiếu, ngày, khách, số tiền.
 * Dữ liệu đến từ `accounting.documents[]` lọc `cash_receipt` — cùng nguồn với khối "Chứng từ
 * kế toán", nên hai khối không thể lệch nhau.
 *
 * KHÔNG hiện trạng thái đồng bộ AMIS ở đây. Đẩy chứng từ sang AMIS là việc của phân hệ Kế
 * toán — người bán hàng đứng ở màn đơn không làm gì được với nhãn đó, mà nhãn đỏ "Không đồng
 * bộ" cạnh một phiếu đã ghi sổ thành công lại đọc thành "phiếu thu hỏng".
 */
export interface MockReceipt {
  id: string;
  code: string;
  /** Id chứng từ — dựng link sang trang phiếu thu. Thiếu thì hiện mã trần, không link chết. */
  docId?: string;
  /** Nhãn hình thức thu. Không xác định được thì bỏ trống, không mặc định thành CK. */
  type?: 'TM' | 'CK';
  date?: string;
  customerInfo?: string;
  amount: number;
}

interface OrderReceiptsListProps {
  receipts: MockReceipt[];
  /**
   * Chỉ truyền khi người dùng THẬT SỰ được xoá (quyền `orders.receipts.delete`) — không có
   * hàm thì không có nút. Nhận `id` của phiếu (= `doc_id` chứng từ) và tự gọi API + báo kết
   * quả; component này không báo thay, vì nó không biết BE trả 200 hay 403.
   */
  onDeleteReceipt?: (id: string) => void;
  onCreateReceipt?: () => void;
  /**
   * Tổng tiền đơn hàng (`order.total_amount`, map từ `summary.total`) — mốc so sánh của lưu
   * ý "thu vượt". Không truyền (hoặc 0) thì KHÔNG cảnh báo: đơn chưa biết tổng tiền mà báo
   * vượt là báo động giả ở mọi đơn, người dùng sẽ học cách bỏ qua.
   */
  orderTotal?: number;
}

export function OrderReceiptsList({ receipts, onDeleteReceipt, onCreateReceipt, orderTotal }: OrderReceiptsListProps) {
  const totalCollected = receipts.reduce((sum, r) => sum + r.amount, 0);
  // Thu vừa đủ (bằng nhau) KHÔNG phải thu vượt — dùng `>` chứ không `>=`.
  const isOverCollected = Number(orderTotal) > 0 && totalCollected > Number(orderTotal);

  return (
    <Card
      title="Phiếu thu"
      size="small"
      extra={
        onCreateReceipt && <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onCreateReceipt}>
          Tạo phiếu thu
        </Button>
      }
    >
      <List
        dataSource={receipts}
        renderItem={(item) => (
          <List.Item
            actions={onDeleteReceipt ? [
              <Popconfirm
                title="Xoá phiếu thu này?"
                description="Chứng từ sẽ được huỷ, bút toán bị đảo và tiền đã thu của đơn trừ lại tương ứng."
                onConfirm={() => onDeleteReceipt(item.id)}
                okText="Xóa"
                cancelText="Hủy"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" type="text" danger icon={<DeleteOutlined />}>
                  Xóa
                </Button>
              </Popconfirm>,
            ] : undefined}
          >
            <List.Item.Meta
              style={{ flex: 1, marginRight: 16, minWidth: 0 }}
              title={
                <Space wrap>
                  {item.type && <Tag color="default" style={{ fontWeight: 600 }}>{item.type}</Tag>}
                  {item.docId ? (
                    <a
                      href={ROUTES.accounting.receiptEdit(item.docId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 600, fontSize: 14 }}
                    >
                      {item.code}
                    </a>
                  ) : (
                    <Text strong style={{ fontSize: 14 }}>{item.code}</Text>
                  )}
                </Space>
              }
              description={
                <div style={{ marginTop: 4, fontSize: 12, lineHeight: '1.6em' }}>
                  <div style={{ wordBreak: 'break-word' }}>
                    <Text type="secondary">CT: </Text>
                    <Text>{item.date}</Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>KH: </Text>
                    <Text>{item.customerInfo}</Text>
                  </div>
                </div>
              }
            />
            <div style={{ fontSize: 16, fontWeight: 600, color: '#a4193d', paddingRight: 16, flexShrink: 0, whiteSpace: 'nowrap' }}>
              {formatVND(item.amount)}
            </div>
          </List.Item>
        )}
        locale={{
          emptyText: (
            <div style={{ color: '#6b6b6b', textAlign: 'center', padding: '24px 0' }}>
              Chưa có phiếu thu nào được tạo cho đơn hàng này.
            </div>
          ),
        }}
      />
      {receipts.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', paddingRight: 16 }}>
          <strong style={{ fontSize: 14 }}>Tổng cộng đã thu:</strong>
          <strong style={{ fontSize: 18, color: '#a4193d' }}>{formatVND(totalCollected)}</strong>
        </div>
      )}
      {isOverCollected && (
        <Alert
          type="warning"
          showIcon={false}
          style={{ marginTop: 12, marginRight: 16 }}
          message={
            <span style={{ fontSize: 13 }}>
              <Text strong>Lưu ý: </Text>
              Tổng tiền phiếu thu đang lớn hơn tiền đơn hàng
              <Text type="secondary" style={{ marginLeft: 6 }}>
                (thu {formatVND(totalCollected)} / đơn {formatVND(Number(orderTotal))})
              </Text>
            </span>
          }
        />
      )}
    </Card>
  );
}
